import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import GroupIcon from '@mui/icons-material/Group';
import PlaceIcon from '@mui/icons-material/Place';
import VideocamIcon from '@mui/icons-material/Videocam';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import {
  Box,
  Chip,
  Container,
  Stack,
  Typography,
  useTheme,
  alpha,
  Button,
} from '@mui/material';
import { memo } from 'react';

import type { Meeting, Group } from '@penticton-dev-events/shared';

export type EventHeroProps = {
  readonly meeting: Meeting;
  readonly group?: Group;
  readonly attendingCount: number;
  readonly onGroupClick?: () => void;
};

/**
 * EventHero displays the main meeting information with an optional poster background.
 * Features responsive design, accessibility support, and elegant visual hierarchy.
 */
const EventHero = memo<EventHeroProps>(
  ({ meeting, group, attendingCount, onGroupClick }) => {
    const theme = useTheme();
    const startDate = new Date(meeting.startsAt);
    const endDate = new Date(meeting.endsAt);
    const spotsRemaining = meeting.capacity ? meeting.capacity - attendingCount : null;

    // Determine if we should show poster background
    const hasPoster = Boolean(meeting.posterUrl);

    return (
      <Box
        sx={{
          position: 'relative',
          background: hasPoster
            ? `linear-gradient(
              135deg,
              ${alpha(theme.palette.primary.dark, 0.8)} 0%,
              ${alpha(theme.palette.primary.main, 0.6)} 100%
            ), url(${meeting.posterUrl}) center/cover`
            : `linear-gradient(
              135deg,
              ${theme.palette.primary.dark} 0%,
              ${theme.palette.primary.main} 100%
            )`,
          color: theme.palette.primary.contrastText,
          py: { xs: 4, md: 6 },
          minHeight: { xs: 300, md: 400 },
        }}
      >
        <Container maxWidth="lg">
          <Stack
            spacing={3}
            sx={{
              position: 'relative',
              zIndex: 2,
            }}
          >
            {/* Status and Group Badge */}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={meeting.status === 'scheduled' ? 'Upcoming' : 'Cancelled'}
                color={meeting.status === 'scheduled' ? 'success' : 'error'}
                size="small"
                sx={{
                  bgcolor:
                    meeting.status === 'scheduled'
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  color:
                    meeting.status === 'scheduled'
                      ? theme.palette.success.contrastText
                      : theme.palette.error.contrastText,
                }}
              />
              {/* Event Type Badge */}
              <Chip
                icon={
                  meeting.eventType === 'in-person' ? (
                    <PlaceIcon />
                  ) : meeting.eventType === 'online' ? (
                    <VideocamIcon />
                  ) : (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <PlaceIcon fontSize="small" />
                      <VideocamIcon fontSize="small" />
                    </Box>
                  )
                }
                label={
                  meeting.eventType === 'in-person'
                    ? 'In-Person Event'
                    : meeting.eventType === 'online'
                      ? 'Online Event'
                      : 'Hybrid Event'
                }
                size="small"
                sx={{
                  bgcolor:
                    meeting.eventType === 'in-person'
                      ? theme.palette.info.main
                      : meeting.eventType === 'online'
                        ? theme.palette.secondary.main
                        : theme.palette.warning.main,
                  color:
                    meeting.eventType === 'in-person'
                      ? theme.palette.info.contrastText
                      : meeting.eventType === 'online'
                        ? theme.palette.secondary.contrastText
                        : theme.palette.warning.contrastText,
                }}
              />
              {/* Approval Required Badge */}
              {meeting.requiresApproval && (
                <Chip
                  icon={<VerifiedUserIcon />}
                  label="Approval Required"
                  size="small"
                  sx={{
                    bgcolor: theme.palette.warning.main,
                    color: theme.palette.warning.contrastText,
                  }}
                />
              )}
              {group && (
                <Chip
                  label={group.name}
                  variant="outlined"
                  size="small"
                  clickable={Boolean(onGroupClick)}
                  onClick={onGroupClick}
                  sx={{
                    borderColor: theme.palette.primary.contrastText,
                    color: theme.palette.primary.contrastText,
                    '&:hover': onGroupClick
                      ? {
                          bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
                          borderColor: theme.palette.primary.contrastText,
                        }
                      : {},
                  }}
                />
              )}
              {meeting.tags &&
                meeting.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: alpha(theme.palette.primary.contrastText, 0.5),
                      color: alpha(theme.palette.primary.contrastText, 0.8),
                      fontSize: '0.75rem',
                    }}
                  />
                ))}
            </Stack>

            {/* Main Title */}
            <Typography
              variant="h3"
              component="h1"
              fontWeight="bold"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.2,
                textShadow: hasPoster ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              {meeting.title}
            </Typography>

            {/* Key Details Grid */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mt: 3 }}>
              {/* Date & Time */}
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <CalendarTodayIcon sx={{ mt: 0.5, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    {startDate.toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {startDate.toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}{' '}
                    -{' '}
                    {endDate.toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              </Stack>

              {/* Location */}
              {meeting.location && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <LocationOnIcon sx={{ mt: 0.5, opacity: 0.9 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="medium">
                      {meeting.location.label}
                    </Typography>
                    {meeting.location.address && (
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {meeting.location.address}
                      </Typography>
                    )}
                    {meeting.location.mapUrl && (
                      <Button
                        component="a"
                        href={meeting.location.mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="text"
                        sx={{
                          color: 'inherit',
                          textDecoration: 'underline',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.875rem',
                        }}
                      >
                        View on map
                      </Button>
                    )}
                  </Box>
                </Stack>
              )}

              {/* Online Meeting */}
              {meeting.onlineUrl && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <VideoCallIcon sx={{ mt: 0.5, opacity: 0.9 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="medium">
                      Online Meeting
                    </Typography>
                    <Button
                      component="a"
                      href={meeting.onlineUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="text"
                      sx={{
                        color: 'inherit',
                        textDecoration: 'underline',
                        p: 0,
                        minWidth: 'auto',
                      }}
                    >
                      Join Video Call
                    </Button>
                  </Box>
                </Stack>
              )}

              {/* Attendance */}
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <GroupIcon sx={{ mt: 0.5, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    {attendingCount} Attending
                  </Typography>
                  {spotsRemaining !== null && (
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {spotsRemaining > 0
                        ? `${spotsRemaining} spots remaining`
                        : 'Event is full'}
                    </Typography>
                  )}
                  {meeting.requiresApproval && (
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, fontStyle: 'italic' }}
                    >
                      RSVPs require approval
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Stack>

            {/* Co-hosts */}
            {meeting.coHosts && meeting.coHosts.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1 }}>
                  Co-hosted by:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {meeting.coHosts.map((coHost, index) => (
                    <Chip
                      key={`${coHost.uid}-${index}`}
                      label={`${coHost.displayName}${coHost.role ? ` (${coHost.role})` : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: alpha(theme.palette.primary.contrastText, 0.5),
                        color: alpha(theme.palette.primary.contrastText, 0.9),
                        '& .MuiChip-label': {
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Container>

        {/* Poster Alt Text for Screen Readers */}
        {meeting.posterAlt && hasPoster && (
          <Box
            component="div"
            role="img"
            aria-label={meeting.posterAlt}
            sx={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          />
        )}
      </Box>
    );
  },
);

EventHero.displayName = 'EventHero';

export default EventHero;
